import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { Box, CircularProgress, styled, Typography } from "@mui/material";
import {
  BottomActionArea,
  BottomActionButton,
  EmailInput,
  FormBody,
  FormContentContainer,
  FormHeader,
  NavigateBeforeButton,
  PhoneInput,
  SnackBar,
} from "@components";
import { useAuth } from "@contexts";
import { useUpdateDoctorContact } from "@hooks";
import { AuthFlow, AuthMethod, Doctor } from "@interfaces";
import {
  getBirthdateInfo,
  parseRNPIDoctorBirthdate,
  separateDoctorSpecialty,
} from "@utils";
import { format } from "rut.js";

const FormContainer = styled("form")({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  flex: 1,
});

const SectionContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  padding: "16px",
  gap: "8px",
  backgroundColor: theme.palette.background.default,
  borderRadius: "4px",
  border: `1px solid ${theme.palette.text.lightGray}`,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 600,
  color: theme.palette.text.black,
}));

const FieldText = styled(Typography)(({ theme }) => ({
  textAlign: "left",
  color: theme.palette.text.black,
}));

export interface UpdateContactLocationState {
  doctor: Doctor;
  authMethod: AuthMethod;
  isNewAccount: boolean;
}

interface ContactFormValues {
  email: string;
  phone: string;
}

export function UpdateContactScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { doctor, authMethod, isNewAccount }: UpdateContactLocationState =
    location.state || {};

  const {
    rut = "",
    name = "",
    lastName = "",
    speciality = "",
    birthdate = "",
    registrationNumber = "",
  } = doctor || {};

  const parsedBirthdate = parseRNPIDoctorBirthdate(birthdate);
  const { years: age } = getBirthdateInfo(birthdate);
  const { mekidocAccessTitle, specialties } =
    separateDoctorSpecialty(speciality);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    trigger,
    clearErrors,
  } = useForm<ContactFormValues>({
    defaultValues: {
      email: doctor?.email || "",
      phone: doctor?.phone?.replace(/^\+56/, "") || "",
    },
  });

  const [currentPhoneCode, setCurrentPhoneCode] = useState({
    name: "Chile",
    icon: "🇨🇱",
    prefix: "+56",
  });

  const { startAuthChallenge, checkDoctorEmailExists } = useAuth();
  const updateContact = useUpdateDoctorContact({ rut });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const onSubmit: SubmitHandler<ContactFormValues> = useCallback(
    async (data) => {
      setSaving(true);
      setErrorMessage("");
      const fullPhone = currentPhoneCode.prefix + data.phone;
      try {
        if (data.email !== doctor.email) {
          const exists = await checkDoctorEmailExists(data.email);
          if (exists) {
            setErrorMessage("El correo ya está en uso.");
            setSaving(false);
            return;
          }
        }

        await updateContact.mutateAsync({
          email: data.email,
          phone: fullPhone,
        });

        const updatedDoctor: Doctor = {
          ...doctor,
          email: data.email,
          phone: fullPhone,
        };

        const challengeStarted = await startAuthChallenge({
          email: updatedDoctor.email,
          authMethod,
          authFlow: AuthFlow.LOGIN,
        });

        if (!challengeStarted) {
          throw Error("Failed to start auth challenge");
        }

        navigate("/app/codigo", {
          state: {
            doctor: updatedDoctor,
            authMethod,
            isNewAccount,
          },
        });
      } catch (error) {
        console.error("Error updating contact:", error);
        setErrorMessage(
          "Error al actualizar datos de contacto. Intenta nuevamente.",
        );
      } finally {
        setSaving(false);
      }
    },
    [
      doctor,
      authMethod,
      isNewAccount,
      currentPhoneCode,
      updateContact,
      startAuthChallenge,
      navigate,
      checkDoctorEmailExists,
    ],
  );

  if (!doctor) {
    navigate("/app");
    return null;
  }

  return (
    <>
      <FormHeader
        title="Actualizar contacto"
        titleColor="white"
        left={
          <NavigateBeforeButton
            iconColor="white"
            onClick={() =>
              navigate("/app/codigo", {
                state: { doctor, authMethod, isNewAccount },
              })
            }
          />
        }
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <FormContainer onSubmit={handleSubmit(onSubmit)}>
            <SectionContainer>
              <SectionTitle>Tus datos</SectionTitle>
              <FieldText>
                {name} {lastName}
              </FieldText>
              <FieldText>{format(rut)}</FieldText>
              {parsedBirthdate && (
                <FieldText>
                  {age} años · {parsedBirthdate}
                </FieldText>
              )}
              <FieldText>
                {mekidocAccessTitle} (REG-SIS N°: {registrationNumber})
              </FieldText>
              {specialties && <FieldText>{specialties}</FieldText>}
            </SectionContainer>

            <EmailInput
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              trigger={trigger}
            />

            <PhoneInput
              register={register}
              errors={errors}
              watch={watch}
              setValue={setValue}
              trigger={trigger}
              clearErrors={clearErrors}
              phoneCodeState={{
                currentPhoneCode,
                setCurrentPhoneCode,
              }}
              showTooltip={true}
            />

            <BottomActionArea>
              <BottomActionButton type="submit" disabled={saving}>
                {saving ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Guardar y reenviar código"
                )}
              </BottomActionButton>
            </BottomActionArea>
          </FormContainer>

          <SnackBar
            snackBarState={{
              open: !!errorMessage,
              type: "error",
            }}
            onClose={() => setErrorMessage("")}
            alerts={{
              error: errorMessage,
            }}
          />
        </FormBody>
      </FormContentContainer>
    </>
  );
}
